import { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";
import { Reveal } from "./Reveal";
import { BUSINESS, STATS } from "@/data/business";

const Counter = ({ to, suffix = "", duration = 1.8 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration,
      ease: [0.21, 0.47, 0.32, 0.98],
      onUpdate: (v) => setValue(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, to, duration]);

  return (
    <span ref={ref} className="font-display text-4xl sm:text-5xl font-semibold text-[color:var(--nitc-ink)]">
      {value.toLocaleString("en-IN")}
      <span className="text-[color:var(--nitc-accent)]">{suffix}</span>
    </span>
  );
};

const About = () => {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-24 sm:py-32 bg-[color:var(--nitc-bg-2)] overflow-hidden"
    >
      {/* Faded background number */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center select-none">
        <motion.p
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.2 }}
          className="font-display text-[24rem] sm:text-[30rem] leading-none font-semibold whitespace-nowrap"
          style={{ color: "rgba(30,30,30,0.04)" }}
        >
          25+
        </motion.p>
      </div>

      <div className="container-nitc relative">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <Reveal className="lg:col-span-5">
            <p className="text-eyebrow">About Us</p>
            <h2 className="mt-5 font-display text-4xl sm:text-5xl font-semibold text-[color:var(--nitc-ink)] leading-[1.05] tracking-tight">
              25+ Years of Quietly Holding Up Spaces.
            </h2>
            <div className="mt-8 h-px w-16 bg-[color:var(--nitc-accent)]" />
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-7 space-y-6">
            <p className="text-[color:var(--nitc-text)] text-base sm:text-lg leading-relaxed">
              Founded by <span className="text-[color:var(--nitc-ink)] font-medium">{BUSINESS.founder}</span>,
              {" "}{BUSINESS.name} is a {BUSINESS.type.toLowerCase()} dealer of plywood, block boards,
              formica sheets, flush doors, shuttering plywood and packing plywood — operating out of
              Nagawara, Bangalore for more than two and a half decades.
            </p>
            <p className="text-[color:var(--nitc-text)] text-base sm:text-lg leading-relaxed">
              What started as a single counter has grown into a trusted name across packaging giants,
              interior designers, builders and homeowners. Our edge is simple: premium-quality stock
              from India's best mills, competitive dealer pricing, dependable bulk supply and a team
              that picks up the phone.
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-8 pt-8">
              {STATS.map((s) => (
                <div key={s.label} data-testid={`about-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <Counter to={s.value} suffix={s.suffix} />
                  <p className="mt-2 text-xs sm:text-sm text-[color:var(--nitc-muted)] uppercase tracking-[0.18em]">
                    {s.label}
                  </p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default About;
